
import { Mail, Phone, MapPin, Send, Github, Linkedin } from "lucide-react";
import React, {useState} from "react";
import { useTranslation } from 'react-i18next';
import { trackContactFormStart, trackContactFormComplete, trackSocialClick } from '../utils/analytics';

const Contact = () => {
    const { t } = useTranslation();
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        projectType: "",
        message: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Track form submission attempt
        trackContactFormComplete(formData.projectType || 'not_specified');

        try {
            const recaptchaToken = await new Promise<string>((resolve, reject) => {
                (window as any).grecaptcha.enterprise.ready(async () => {
                    try {
                        const token = await (window as any).grecaptcha.enterprise.execute(
                            '6LeuA7wsAAAAAOQN_JIPngl_xNjgQyx8CdmeBPE0',
                            { action: 'CONTACT' }
                        );
                        resolve(token);
                    } catch (err) {
                        reject(err);
                    }
                });
            });

            const res = await fetch("/contact.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...formData, recaptchaToken }),
            });
            if (res.ok) {
                setPopupMessage(t('contact.messages.success'));
                setFormData({ name: "", email: "", projectType: "", message: "" });
            } else {
                setPopupMessage(t('contact.messages.error'));
            }
        } catch (err) {
            console.error(err);
            setPopupMessage(t('contact.messages.serverError'));
        }

        setShowPopup(true);

        setTimeout(() => {
            setShowPopup(false);
        }, 3000);
    };

    return (
        <section id="contact" className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
            {showPopup && (
                <div className="fixed inset-x-0 top-4 mx-auto w-fit bg-white/95 text-black py-3 px-6 rounded-xl shadow-2xl z-50 transition-opacity animate-fade-in">
                    <p className="text-center font-medium">{popupMessage}</p>
                </div>
            )}
            <div className="container mx-auto px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            {t('contact.title') || 'Let\'s Work Together'}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            {t('contact.subtitle')}
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-8">{t('contact.getInTouch')}</h3>

                            <div className="space-y-6 mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                                        <Mail className="w-6 h-6 rounded-lg" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">{t('contact.email')}</div>
                                        <div className="text-gray-300">finfratech@finfratech.ee</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                                        <Phone className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">{t('contact.phone')}</div>
                                        <div className="text-gray-300">+372 5628 1472</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                                        <MapPin className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">{t('contact.location')}</div>
                                        <div className="text-gray-300">{t('contact.locationValue')}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <a 
                                    href="https://github.com/MathiasRanna" 
                                    target='_blank' 
                                    className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors" 
                                    rel="noreferrer"
                                    onClick={() => trackSocialClick('github', 'https://github.com/MathiasRanna')}
                                >
                                    <Github className="w-6 h-6" />
                                </a>
                                <a
                                    href="https://www.linkedin.com/in/mathias-ranna-592063228/"
                                    target='_blank'
                                    className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
                                    rel="noreferrer"
                                    onClick={() => trackSocialClick('linkedin', 'https://www.linkedin.com/in/mathias-ranna-592063228/')}
                                >
                                    <Linkedin className="w-6 h-6" />
                                </a>
                                <a
                                    href="https://wa.me/37256281472"
                                    target='_blank'
                                    className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
                                    rel="noreferrer"
                                    onClick={() => trackSocialClick('whatsapp', 'https://wa.me/37256281472')}
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                </a>
                            </div>
                        </div>

                        <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20">
                            <form onSubmit={handleSubmit} className="space-y-6" onFocus={() => trackContactFormStart()}>
                                <div>
                                    <label className="block text-sm font-medium mb-2">{t('contact.form.name')}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name} onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:border-blue-400 transition-colors placeholder-gray-400"
                                        placeholder={t('contact.form.namePlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">{t('contact.form.email')}</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email} onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:border-blue-400 transition-colors placeholder-gray-400"
                                        placeholder={t('contact.form.emailPlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">{t('contact.form.projectType')}</label>
                                    <select name="projectType" value={formData.projectType} onChange={handleChange} className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:border-blue-400 transition-colors text-white">
                                        <option value="" className="text-gray-900">{t('contact.form.selectService')}</option>
                                        <option value="ai" className="text-gray-900">{t('contact.form.aiIntegration')}</option>
                                        <option value="software" className="text-gray-900">{t('contact.form.softwareDevelopment')}</option>
                                        <option value="ecommerce" className="text-gray-900">{t('contact.form.ecommerce')}</option>
                                        <option value="web" className="text-gray-900">{t('contact.form.webDevelopment')}</option>
                                        <option value="wordpress" className="text-gray-900">{t('contact.form.wordpress')}</option>
                                        <option value="cloud" className="text-gray-900">{t('contact.form.awsCloudSolutions')}</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">{t('contact.form.message')}</label>
                                    <textarea
                                        rows={4}
                                        name="message"
                                        value={formData.message} onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:border-blue-400 transition-colors placeholder-gray-400 resize-none"
                                        placeholder={t('contact.form.messagePlaceholder')}
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 group"
                                >
                                    {t('contact.form.sendMessage')}
                                    <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
